import React from 'react';
import { Link } from 'react-router-dom';
import { Play, Clock } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import { VIDEOS, formatDuration } from '../data/videoIndex';

// Index for the /videos/* watch pages. Gives the watch pages an internal
// crawl path (the video sitemap alone leaves them orphaned) and a place for
// the nav to point at.
const VideosPage = () => {
  const canonical = 'https://www.getsortio.com/videos';

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
        { '@type': 'ListItem', position: 2, name: 'Videos', item: canonical },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: VIDEOS.map((v, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://www.getsortio.com${v.watchPath}`,
        name: v.title,
      })),
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121212' }}>
      <SEOHead
        title="Sortio Video Tutorials: AI File Organization Demos"
        description="Short demos of Sortio organizing real folders: messy Downloads, a 200-file desktop, sorting by file contents, automatic rules, Spaces, and using your own API key."
        canonical={canonical}
        schema={schema}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={[{ label: 'Videos' }]} />

        <h1 className="text-4xl md:text-5xl font-semibold text-apple-50 mb-4">
          Sortio Videos
        </h1>
        <p className="text-lg text-apple-300 mb-12 max-w-2xl">
          Short, unedited demos of Sortio working on real folders. Each one has
          a full transcript.
        </p>

        <div className="grid gap-8 sm:grid-cols-2">
          {VIDEOS.map((video) => {
            const readable = formatDuration(video.duration);
            return (
              <Link
                key={video.slug}
                to={video.watchPath}
                className="group block rounded-2xl overflow-hidden border border-apple-700/40 bg-apple-800/30 hover:border-sortio-primary/50 transition-colors"
              >
                <div className="relative">
                  <img
                    src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`}
                    alt=""
                    loading="lazy"
                    className="w-full aspect-video object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-black/60 flex items-center justify-center group-hover:bg-sortio-primary/80 transition-colors">
                      <Play className="text-white ml-1" size={22} />
                    </div>
                  </div>
                  {readable && (
                    <span className="absolute bottom-2 right-2 flex items-center px-2 py-1 rounded bg-black/75 text-white text-xs">
                      <Clock className="mr-1" size={12} />
                      {readable}
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <h2 className="text-lg font-medium text-apple-50 mb-2 group-hover:text-sortio-primary transition-colors">
                    {video.title}
                  </h2>
                  <p className="text-sm text-apple-400 line-clamp-3">
                    {video.description}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default VideosPage;
